// @ts-nocheck
import { useState } from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useColors } from '../../theme/ColorModeContext';
import { academyTracks } from '../Academy/skillTreeData';
import { categoryToText } from '../../utils/Category';

interface Stats {
  category: string;
  challenges: number;
  solved: number;
}

interface CategoryData {
  category: string;
  subject: string;
  label: string;
  value: number;
  solved: number;
  total: number;
}

interface SkillRadarProps {
  data: CategoryData[];
}

const skillProfileTracks = academyTracks.map((track) => ({
  value: track.slug,
  label: track.title,
}));

const categoriesByTrack = academyTracks.reduce((tracks: Record<string, Set<string>>, track) => {
  tracks[track.slug] = new Set(
    track.tiers.flatMap((tier) => tier.nodes.map((node) => node.slug))
  );
  return tracks;
}, {});

const shortCategoryLabels: Record<string, string> = {
  introduction: 'Intro',
  'access-control': 'Access Ctrl',
  'denial-of-service': 'DoS',
  upgradeability: 'Upgrades',
  'gas-optimization': 'Gas Opt',
  'daml-authorization': 'Authz',
  'daml-availability': 'Avail.',
  'daml-keys-and-uniqueness': 'Keys',
  'daml-state-integrity': 'State',
  'daml-offledger-integration': 'Off-Ledger',
  'daml-time-and-boundaries': 'Time',
  'solana-security-account-authority': 'Acct/Auth',
  'solana-security-cpi-and-accounting': 'CPI/Acctg',
  'solana-security-state-time-availability': 'State/Time',
  'solana-anchor-security-cpi-remaining-accounts': 'CPI/Rem.',
  'solana-anchor-security-lifecycle-governance': 'Lifecycle',
  'bitcoin-foundation': 'Found.',
  'bitcoin-transactions': 'Tx',
  'bitcoin-timelocks': 'Locks',
  'bitcoin-signatures': 'Sighash',
  'cairo-foundation': 'Cairo',
  'cairo-starknet-contracts': 'Contracts',
  'cairo-security-access-control': 'Access',
  'cairo-security-accounting': 'Acctg',
  'cairo-security-state-and-lifecycle': 'State',
  'cairo-security-cross-contract': 'X-Contract',
  'cairo-security-signatures-and-accounts': 'Sigs',
  'cairo-security-denial-of-service': 'DoS',
  'cairo-security-upgrades-and-deployment': 'Upgrades',
  'move-foundation': 'Move',
  'move-sui-objects': 'Objects',
  'move-sui-real-objects': 'Real Obj',
  'move-sui-security-access-control': 'Access',
  'move-sui-security-accounting': 'Acctg',
  'move-sui-security-state-lifecycle': 'Lifecycle',
  'move-sui-security-object-model': 'Obj Model',
};

const categoryOrder = academyTracks
  .flatMap((track) => track.tiers.flatMap((tier) => tier.nodes.map((node) => node.slug)))
  .reduce((order: Record<string, number>, category, index) => {
    order[category] = index;
    return order;
  }, {
    season1: 1000,
    missions: 1001,
  } as Record<string, number>);

const getRadarLabel = (category: string) => (
  shortCategoryLabels[category] || categoryToText(category) || category
);

const getFullLabel = (category: string) => categoryToText(category) || category;

const getCategoryValue = (stats: Stats) => (
  stats.challenges > 0 ? Math.round((stats.solved / stats.challenges) * 100) : 0
);

const buildSkillRadarData = (stats: Stats[] = []): CategoryData[] => (
  [...stats]
    .sort((a, b) => {
      const aOrder = categoryOrder[a.category] ?? Number.MAX_SAFE_INTEGER;
      const bOrder = categoryOrder[b.category] ?? Number.MAX_SAFE_INTEGER;

      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.category.localeCompare(b.category);
    })
    .map((stat) => ({
      category: stat.category,
      subject: getRadarLabel(stat.category),
      label: getFullLabel(stat.category),
      value: getCategoryValue(stat),
      solved: stat.solved,
      total: stat.challenges,
    }))
);

const CustomTooltip = ({ active, payload }: any) => {
  const colors = useColors();
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <Box sx={{
        backgroundColor: colors.bg.secondary,
        border: `1px solid ${colors.border.accent}`,
        borderRadius: '8px',
        padding: '8px 12px',
      }}>
        <Typography sx={{ fontSize: '12px', color: colors.accent.cyan, fontWeight: 600 }}>
          {d.label}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>
          {d.solved} / {d.total} solved
        </Typography>
      </Box>
    );
  }
  return null;
};

const SkillRadar = ({ data }: SkillRadarProps) => {
  const colors = useColors();
  const [selectedTrack, setSelectedTrack] = useState(skillProfileTracks[0].value);
  const selectedTrackCategories = categoriesByTrack[selectedTrack] || new Set();
  const filteredData = data.filter((item) => selectedTrackCategories.has(item.category));

  return (
    <Box sx={{
      '& .recharts-surface:focus': {
        outline: 'none',
      },
      '& .recharts-surface:focus-visible': {
        outline: `1px solid ${colors.border.accent}`,
        outlineOffset: '-2px',
      },
      '& .recharts-surface *:focus': {
        outline: 'none',
      },
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '4px',
        flexWrap: 'wrap',
      }}>
        <Typography variant="overline" sx={{
          color: colors.text.muted,
          fontSize: '10px',
          letterSpacing: '0.12em',
          display: 'block',
        }}>
          Skill Profile
        </Typography>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 178 } }}>
          <Select
            value={selectedTrack}
            onChange={(event) => setSelectedTrack(event.target.value)}
            sx={{
              height: '30px',
              color: colors.text.secondary,
              fontFamily: '"Share Tech Mono", monospace',
              fontSize: '11px',
              backgroundColor: colors.bg.secondary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.subtle,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.accent,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.accent.cyan,
              },
              '& .MuiSvgIcon-root': {
                color: colors.accent.cyan,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: colors.bg.secondary,
                  border: `1px solid ${colors.border.subtle}`,
                  color: colors.text.secondary,
                },
              },
            }}
          >
            {skillProfileTracks.map((track) => (
              <MenuItem key={track.value} value={track.value} sx={{
                fontFamily: '"Share Tech Mono", monospace',
                fontSize: '12px',
              }}>
                {track.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {filteredData.length > 0 ? (
        <ResponsiveContainer width="100%" height={260}>
          <RadarChart
            data={filteredData}
            margin={{ top: 10, right: 20, bottom: 10, left: 20 }}
            onMouseDown={(event) => event.preventDefault()}
          >
            <defs>
              <linearGradient id="radarFill" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={colors.accent.cyan} stopOpacity={0.35} />
                <stop offset="100%" stopColor={colors.accent.purple} stopOpacity={0.35} />
              </linearGradient>
            </defs>
            <PolarGrid
              stroke={colors.border.subtle}
              strokeOpacity={0.8}
            />
            <PolarAngleAxis
              dataKey="subject"
              tick={{
                fill: colors.text.secondary,
                fontSize: 11,
                fontFamily: '"Orbitron", sans-serif',
              }}
            />
            <Radar
              name="Skills"
              dataKey="value"
              stroke={colors.accent.cyan}
              strokeWidth={1.5}
              fill="url(#radarFill)"
              dot={{ fill: colors.accent.cyan, r: 3, strokeWidth: 0 }}
            />
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{
          height: 260,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px dashed ${colors.border.subtle}`,
          borderRadius: '8px',
          backgroundColor: `${colors.bg.secondary}66`,
        }}>
          <Typography sx={{
            color: colors.text.muted,
            fontFamily: '"Share Tech Mono", monospace',
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}>
            No stats yet
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SkillRadar;
export { buildSkillRadarData };
export type { CategoryData, Stats as SkillRadarStats };
